import React, { useState, useEffect } from 'react';
import { Grid, Link } from '@material-ui/core';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { handleErrorToast } from '../../shared/Functions';
import { ReportsVService } from '../../../services/V2/gozer';
import { PortalConfigsVService } from '../../../services/V2/padre';
import Papa from 'papaparse';
import { BankReconciliationHeaders } from '../../../services/BankReconciliation';

export const BankReconciliationResult = ({ model, useDistricts, errorToast, setLoading, operatorId }) => {
    const [reportData, setReportData] = useState<any>();
    const [offsetInMinutes, setOffsetInMinutes] = useState<number>(-new Date().getTimezoneOffset());

    useEffect(() => {
        if (operatorId) {
            PortalConfigsVService.portalConfigsVGetAll(operatorId)
                .then((x: any) => {
                    let offset: number = -new Date().getTimezoneOffset();
                    try {
                        const nowDate = new Date();
                        const tzDate = new Date(nowDate.toLocaleString('en-US', { timeZone: x.timezone }));
                        const calculatedOffset = (nowDate.getTime() - tzDate.getTime()) / 60000;
                        offset = -calculatedOffset;
                    } catch (e) {
                    }
                    setOffsetInMinutes(offset);
                })
                .catch((x) => { });
        }
    }, [operatorId]);

    useEffect(() => {
        setLoading(true)
        const sendModel = { ...model };
        ReportsVService.reportsVGetReconciliationExport(offsetInMinutes, useDistricts, sendModel)
        .then((x: any) => {
            setReportData(x)
            setLoading(false)
        })
        .catch(err => {
            errorToast(handleErrorToast(err))
            setLoading(false)
        })
    }, [model, offsetInMinutes]) // eslint-disable-line react-hooks/exhaustive-deps

    const readCsvData = (): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            Papa.parse(reportData, {
                header: false,
                skipEmptyLines: false,
                complete: function (results) {
                    const data: any[] = results.data ?? [];
                    resolve(data);
                },
                error: function (error) {
                    errorToast(`There was an error processing your file: ${error}. Please try again.`);
                    reject(error);
                }
            });
        });
    };

	const handleDownload = async () => {
        const parsedData = await readCsvData();

        // Create a new workbook and add the parsed data
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        worksheet.addRows(parsedData ?? []);
        worksheet.insertRow(4, []);
        worksheet.insertRow(5, []);

        const headers = ["FundingDate","FundingGroup","PaymentDescription","BatchNumber","Count","BatchAmount","i3CreditCard","i3ACH"];

        const reportTitleRows = [1, 2, 3];
        const districtNameRows: number[] = [];
        let headerRowNumber = 0;

        // Setup default fault
        worksheet.eachRow((row, rowNumber) => {
            row.eachCell(cell => {
                cell.font = { name: "Arial" };
            });
        });

        // Rows formatting
        worksheet.eachRow((row, rowNumber) => {
            // Headers formatting
            const rowValues = Array.isArray(row.values) ? row.values.slice(1) : Object.values(row.values).slice(1);
            if (rowValues.length === headers.length && rowValues.every((value, index) => value === headers[index])) {
                headerRowNumber = rowNumber;
                row.eachCell(cell => {
                    cell.value = BankReconciliationHeaders.find(x => x.value === cell.value)?.name
                    cell.font = { 
                        name: "Arial",
                        bold: true,
                        size: 11
                    };
                    cell.alignment = { 
                        vertical: 'middle',
                        horizontal: 'center'
                    };
                    cell.border = {
                        bottom: {
                            style:'thin',
                            color: {
                                argb:'000000'
                            }
                        }
                    }
                });
            };
            // District Names formatting
            if (rowValues?.some(cell => String(cell).includes("District:"))) {
                districtNameRows.push(rowNumber)
                row.eachCell(cell => {
                    cell.font = { 
                        name: "Arial",
                        size: 11,
                        bold: true
                    };
                    cell.alignment = { 
                        vertical: 'middle'
                    };
                });
            };
            // Report title formatting
            if (rowValues?.some(cell => String(cell).includes("Bank Reconciliation Report"))) {
                row.eachCell(cell => {
                    cell.font = { name: "Arial", size: 16, bold: true };
                });
            };
            // Totals formatting
            if (rowValues.some(cell => String(cell).includes("Total") && !String(cell).includes("Funding Date") && !String(cell).includes("Grand Total"))) {
                row.eachCell({ includeEmpty: true }, cell => {
                    cell.font = { name: "Arial", bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };

                    if (worksheet.getColumn(cell.fullAddress.col).values.includes("Batch Amount")) {
                        cell.border = {
                            top: {
                                style:'double',
                                color: {
                                    argb:'000000'
                                }
                            }
                        }
                    };
                })
            };
            if (rowValues.some(cell => String(cell).includes("Total") && String(cell).includes("Funding Date"))) {
                for (let i = 1; i <= headers.length; i++) {
                    let cell = row.getCell(i);
                    cell.font = { name: "Arial", bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.border = {
                        top: {
                            style: 'thin',
                            color: {
                                argb: '000000'
                            }
                        }
                    };
                };
            };
            if (rowValues.some(cell => String(cell).includes("Grand Total"))) {
                for (let i = 1; i <= headers.length; i++) {
                    let cell = row.getCell(i);
                    cell.font = { name: "Arial", bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.border = {
                        top: {
                            style:'thin',
                            color: {
                                argb:'000000'
                            }
                        },
                        bottom: {
                            style:'thin',
                            color: {
                                argb:'000000'
                            }
                        }
                    }
                };
            };
            // Deposit verification formatting
            if (rowValues.some(cell => String(cell).includes("Deposit Verification"))) {
                for (let i = 1; i <= headers.length; i++) {
                    let cell = row.getCell(i);
                    cell.font = { name: "Arial", bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                    cell.border = {
                        bottom: {
                            style: 'thin',
                            color: {
                                argb: '000000'
                            }
                        }
                    };
                };
            };
        });

        // Set Count column to Number Format
        const numberFormat = '#,##0';
        const countColumnIndex = headers.indexOf("Count") + 1;
        const countColumn = worksheet.getColumn(countColumnIndex)
        countColumn.eachCell(cell => {
            const value = cell.value;
            const numberValue = Number(value);

            if (!isNaN(numberValue)) {
                if (numberValue !== 0) {
                    cell.value = numberValue;
                    cell.alignment = {
                        horizontal: "left"
                    }
                    cell.numFmt = numberFormat
                }
            } else {
                console.warn(`Could not convert value '${value}' in cell ${cell.address} to a number.`);
            }
        })

        // Set Columns to Accounting Format as necessary
        const accountingFormat = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
        const batchAmountColumnIndex = headers.indexOf("BatchAmount") + 1;
        const batchAmountColumn = worksheet.getColumn(batchAmountColumnIndex)
        const i3CreditCardColumnIndex = headers.indexOf("i3CreditCard") + 1;
        const i3CreditCardColumn = worksheet.getColumn(i3CreditCardColumnIndex)
        const i3AchColumnIndex = headers.indexOf("i3ACH") + 1;
        const i3AchColumn = worksheet.getColumn(i3AchColumnIndex)
        
        batchAmountColumn.eachCell(cell => {
            const value = cell.value;
            if (value !== null && value !== undefined && value !== '') {
                const numberValue = Number(value);

                if (!isNaN(numberValue)) {
                    cell.value = numberValue
                    cell.numFmt = accountingFormat
                } else {
                    console.warn(`Could not convert value '${value}' in cell ${cell.address} to a number.`);
                }
            }
        })

        i3CreditCardColumn.eachCell(cell => {
            const value = cell.value;
            if (value !== null && value !== undefined && value !== '') {
                const numberValue = Number(value);

                if (!isNaN(numberValue)) {
                    cell.value = numberValue
                    cell.numFmt = accountingFormat
                } else {
                    console.warn(`Could not convert value '${value}' in cell ${cell.address} to a number.`);
                }
            }
        })

        i3AchColumn.eachCell(cell => {
            const value = cell.value;
            if (value !== null && value !== undefined && value !== '') {
                const numberValue = Number(value);

                if (!isNaN(numberValue)) {
                    cell.value = numberValue
                    cell.numFmt = accountingFormat
                } else {
                    console.warn(`Could not convert value '${value}' in cell ${cell.address} to a number.`);
                }
            }
        })

        // Adjust column widths based on the content length
        for (let i = 1; i <= worksheet.columns.length; i++) {
            let maxLength = 0;
            const column = worksheet.getColumn(i);
            column.eachCell({ includeEmpty: true }, cell => {
                if (reportTitleRows.includes(cell.fullAddress.row) || districtNameRows.includes(cell.fullAddress.row)) {
                    return;
                }

                let cellValue: string = '';
                if (cell.value) {
                    cellValue = cell.value.toString();
                    maxLength = Math.max(maxLength, cellValue.length);
                }
            });
            column.width = maxLength + 4; // add a little padding
        };

        // Freeze title rows and headers
        worksheet.views = [
            {state: 'frozen', ySplit: headerRowNumber}
        ];
    
        // Write the workbook and save it
        const excelFilename = `Bank Reconciliation Report (i3 Payments) - ${new Date().toLocaleString()}.xlsx`
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), excelFilename);
	}

	return (
		<div>
            <Grid container style={{ display: "flex", justifyContent: "flex-start", margin: "30px 0px 20px 20px", padding: "0px", fontSize: "14px", textAlign: "left" }}>
                <Grid item xs={6}>
                    <b>Download</b>&nbsp;:&nbsp;<Link onClick={handleDownload} className="clickable">Bank Reconciliation Report</Link>
                </Grid>
            </Grid>
		</div>
	);
};